import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Button, Form, Modal } from "semantic-ui-react";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";

import { ApplicationState } from "../../../applicationState";

import { CustomerCategoryFormModel, CustomerCategory } from "../model";
import { customerCategorySelector, routeIdSelector, basePathSelector, customerCategoriesSelector } from "../selectors";
import { saveCustomerCategory } from "../actions";
import { SAVE_CUSTOMERCATEGORY } from "../actiontypes";
import { customerCategorySchemaFactory } from "../schema";

import { CustomerCategoryForm } from "./CustomerCategoryForm";

import { push, PushAction } from "redux-little-router";

export interface StateProps {
    id: string;
    model: CustomerCategoryFormModel;
    open: boolean;
    basePath: string;
    customerCategories: CustomerCategory[];
}

export interface DispatchProps {
    dispatchSave: (customerCategory: CustomerCategoryFormModel, basePath: string) => Promise<void>;
    dispatchClose: (basePath: string) => PushAction;
}

type Props = ReturnType<typeof mergeProps>;

export const EditUnconnected: React.FunctionComponent<Props> = ({ close, open, saveCategory, model, customerCategories }) => {
    const { register, handleSubmit, formState: { errors } } = useForm<CustomerCategoryFormModel>({
        resolver: zodResolver(customerCategorySchemaFactory(customerCategories, model.id)),
        defaultValues: model
    });

    const submit = handleSubmit(saveCategory);

    return (
        <Modal as={Form} onSubmit={submit} size="small" open={open} onClose={close} closeOnDimmerClick={false}>
            <Modal.Header>Edit CustomerCategory</Modal.Header>
            <Modal.Content>
                <CustomerCategoryForm register={register} errors={errors} />
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={close}>Cancel</Button>
                <IsLoadingButton type="submit" requestNames={SAVE_CUSTOMERCATEGORY} content="Save" />
            </Modal.Actions>
        </Modal>
    );
};

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        id: routeIdSelector(state),
        model: customerCategorySelector(state) as CustomerCategoryFormModel,
        open: state.router.pathname.endsWith("/edit"),
        basePath: basePathSelector(state),
        customerCategories: customerCategoriesSelector(state)
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, undefined, any>): DispatchProps {
    return {
        dispatchSave: (customerCategory: CustomerCategoryFormModel, basePath: string) => dispatch(saveCustomerCategory(customerCategory, basePath)),
        dispatchClose: (basePath: string) => dispatch(push(basePath, {}))
    };
}

function mergeProps(propsFromState: StateProps, propsFromDispatch: DispatchProps) {
    return {
        model: propsFromState.model,
        customerCategories: propsFromState.customerCategories,
        open: propsFromState.open,
        close: () => propsFromDispatch.dispatchClose(propsFromState.basePath),
        saveCategory: (model: CustomerCategoryFormModel) => {
            return propsFromDispatch.dispatchSave(model, propsFromState.basePath);
        },
    };
}

export const Edit = connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditUnconnected);
